import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CreditCard } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card.js';
import { Badge } from '../../components/ui/Badge.js';
import { SearchInput } from '../../components/ui/Input.js';
import { CopyButton } from '../../components/ui/CopyButton.js';
import { TableSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchSubscriptions } from '../../api/system.js';
import { formatDate } from '../../lib/utils.js';

export function SubscriptionsPage() {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const { t } = useI18n();

  const { data: subscriptions, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-subscriptions'],
    queryFn: fetchSubscriptions,
  });

  const filteredSubs = (subscriptions || []).filter((s) => {
    if (!search.trim()) return true;
    const term = search.toLowerCase();
    return (
      s.id.toLowerCase().includes(term) ||
      s.user_id.toLowerCase().includes(term) ||
      s.external_subscription_id.toLowerCase().includes(term) ||
      s.product_id.toLowerCase().includes(term)
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            {t('subscriptions.title')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('subscriptions.description')}
          </p>
        </div>
      </div>

      {/* Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="w-full sm:w-80">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('subscriptions.searchPlaceholder')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Subscriptions Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={4} cols={7} />
            </div>
          ) : error ? (
            <div className="p-6">
              <ErrorState title={t('subscriptions.loadError')} message={(error as any).message} onRetry={() => refetch()} />
            </div>
          ) : filteredSubs.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={t('subscriptions.emptyTitle')}
                description={t('subscriptions.emptyDesc')}
              />
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">{t('subscriptions.tableId')}</th>
                  <th className="py-3 px-4">{t('subscriptions.tableUserId')}</th>
                  <th className="py-3 px-4">{t('subscriptions.tableStore')}</th>
                  <th className="py-3 px-4">{t('subscriptions.tableProduct')}</th>
                  <th className="py-3 px-4">{t('subscriptions.tableStatus')}</th>
                  <th className="py-3 px-4">{t('subscriptions.tableCreatedAt')}</th>
                  <th className="py-3 px-4">{t('subscriptions.tableExpiresAt')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredSubs.map((sub) => (
                  <tr key={sub.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="py-3 px-4">
                      <CopyButton value={sub.id} truncate={true} className="font-mono text-indigo-600 dark:text-indigo-400 font-medium" />
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => navigate(`/admin/users/${sub.user_id}`)}
                        className="font-mono text-slate-700 dark:text-slate-300 hover:text-indigo-600 hover:underline"
                      >
                        {sub.user_id}
                      </button>
                    </td>
                    <td className="py-3 px-4 uppercase font-semibold text-[11px] text-slate-600 dark:text-slate-400">
                      {sub.provider}
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-700 dark:text-slate-300">
                      {sub.product_id}
                    </td>
                    <td className="py-3 px-4">
                      <Badge variant={sub.status === 'active' ? 'success' : sub.status === 'trialing' ? 'info' : 'danger'}>
                        {sub.status}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 text-slate-500">
                      {formatDate(sub.started_at)}
                    </td>
                    <td className="py-3 px-4 text-slate-500">
                      {sub.expires_at ? formatDate(sub.expires_at) : 'Ongoing'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
